/**
 * Gemeinsame KI-Nahtstelle ([V] E-a; Anhang A.ai): Vertragseigentümer ist das Vertragsdokument,
 * Implementierung `packages/ai` (W1-14).
 *
 * - `AiPurpose`: 23 Zwecke (E-a Nr. 9 und Nachtrag C-Z1: vier Zwecke der Zielfindung; JDG-0: `jev_judgment` für alle
 *   Jev-Urteile, Unterscheidung über `prompt_version` = Spec-Schlüssel); `tp2_comment` entfällt (E-c).
 * - `RedactedPayload`/`ResearchPayload`/`ProspectSearchPayload` sind über nicht exportierte `unique symbol`-Marken
 *   NICHT per Objektliteral fälschbar (R-4); sie entstehen nur in `packages/ai` per Cast (`ai.brandRedacted`,
 *   `ai.buildResearchPayload`, `ai.buildProspectSearchPayload`). Architekturtest in W1-14.
 * - `research` (Web-Suche, Citations) nimmt bewusst nur `ResearchPayload` (Zweck `tp2_research_search`) bzw.
 *   `ProspectSearchPayload` (Zweck `tp2_prospect_search`, Nachtrag C-Z1) als diskriminierte Vereinigung
 *   (kein allgemeiner Bypass).
 * - Wiederholt werden im Client nur `RETRYABLE_AI_ERRORS` (höchstens 2 Wiederholungen je Aufruf).
 * - `dataMode` (Nachtrag C-C1, vormals C-1) ist Pflichtfeld jedes Aufrufs, nicht Zustand des Clients: `demo` →
 *   kein Anbieteraufruf, Ergebnis `{ ok: false, error: 'permanent', aiCallId: null }`. So können gleichzeitige
 *   Live- und Demo-Sitzungen (und der MCP-Datenmodus) desselben Benutzers nebeneinander bestehen.
 * - `subjectPersonIds` (C-C1, V12-31) ist Pflichtfeld: alle Personen, auf die sich der Aufruf bezieht (`[]` nur ohne
 *   Personenbezug). Die Zeile in `ai_call` entsteht beim Start des Aufrufs mit diesen Bezügen (`subject_person_ids`);
 *   der Abschluss schreibt nur Nutzung, Status und Fehlerklasse, nie Subjektfelder. Der Löschteilnehmer **ai** von
 *   `onPersonErased` entfernt die Bezüge (Kosten- und Nutzungsspalten bleiben).
 */
import { z } from 'zod';
import type { DataMode, DbTx, DomainContext, PersonId, UserId } from './common.js';

declare const redactedBrand: unique symbol;
declare const researchBrand: unique symbol;
declare const prospectSearchBrand: unique symbol;

export const AiPurpose = z.enum([
  'tp2_post_gist',
  'tp2_research_search',
  'tp2_research_structure',
  'tp2_connect_note',
  'tp2_first_message',
  'tp2_intro_request',
  'tp2_anchor_question',
  'tp2_prospect_query',
  'tp2_prospect_rank',
  'tp2_prospect_search',
  'tp2_prospect_structure',
  'tp3_intent',
  'tp3_reply_draft',
  'tp4_audience_fit',
  'tp5_voice_extract',
  'tp5_topic_propose',
  'tp5_scaffold',
  'tp5_draft',
  'tp5_rewrite',
  'tp5_comment_draft',
  'tp5_phrase_assess',
  'tp5_quality_review',
  'jev_judgment',
]);
export const AI_PURPOSES = AiPurpose.options;

export const AI_ERROR_CLASSES = [
  'rate_limited',
  'overloaded',
  'timeout',
  'transient',
  'schema_invalid',
  'max_tokens',
  'refused',
  'auth',
  'budget',
  'web_search_error',
  'permanent',
] as const;
export const RETRYABLE_AI_ERRORS = ['rate_limited', 'overloaded', 'timeout', 'transient'] as const;
export type AiErrorClass = (typeof AI_ERROR_CLASSES)[number];

export type RedactedPayload = {
  readonly [redactedBrand]: true;
  parts: Record<string, string>;
  stats: Record<string, number>;
};
export type ResearchPayload = {
  readonly [researchBrand]: true;
  fields: {
    name: string;
    role?: string;
    organization?: string;
    city?: string;
    purposeCategory: string;
  };
};
/**
 * Nutzlast der Websuche der Zielfindung (Nachtrag C-Z1, Spezifikation 7.2): nur Felder des eigenen Suchprofils (keine
 * Personendaten); `purposeCategory` ist ein Begriff aus einer festen Liste. Sie entsteht nur in
 * `ai.buildProspectSearchPayload` (VB-Z3), das ausschließlich den bestätigten Eingabestand des Laufs liest.
 */
export type ProspectSearchPayload = {
  readonly [prospectSearchBrand]: true;
  fields: {
    icp: string;
    regions: readonly string[];
    orgKinds: readonly string[];
    specialties: readonly string[];
    roleTerms: readonly string[];
    excludeTerms: readonly string[];
    purposeCategory: string;
  };
};
export type Usage = {
  inputTokens: number;
  outputTokens: number;
  webSearchRequests: number;
  costMicroUsd: number;
};

export interface LlmClient {
  parse<T>(r: {
    purpose: z.infer<typeof AiPurpose>;
    promptVersion: string;
    system: string;
    cacheSystemPrefix?: string;
    input: RedactedPayload;
    schema: z.ZodType<T>;
    maxTokens: number;
    effort: 'low' | 'medium' | 'high';
    signal: AbortSignal;
    userId: UserId;
    /** Datenmodus des Aufrufers (C-C1): `demo` erreicht nie den Anbieter. */
    dataMode: DataMode;
    subjectRef?: { type: string; id: string };
    /** Alle betroffenen Personen (C-C1, V12-31); `[]` nur ohne Personenbezug. */
    subjectPersonIds: readonly PersonId[];
  }): Promise<
    | { ok: true; value: T; usage: Usage; aiCallId: string }
    | { ok: false; error: AiErrorClass; aiCallId: string | null }
  >;
  research(
    r: {
      promptVersion: string;
      system: string;
      webSearch: { type: 'web_search_20260209' | 'web_search_20260318'; maxUses: number } & (
        | { allowedDomains: string[] }
        | { blockedDomains: string[] }
      );
      maxTokens: number;
      effort: 'low' | 'medium' | 'high';
      maxContinuations: 3;
      signal: AbortSignal;
      userId: UserId;
      /** Datenmodus des Aufrufers (C-C1): `demo` erreicht nie den Anbieter. */
      dataMode: DataMode;
      /** Alle betroffenen Personen (C-C1, V12-31); `[]` nur ohne Personenbezug. */
      subjectPersonIds: readonly PersonId[];
    } & (
      | { purpose: 'tp2_research_search'; input: ResearchPayload }
      | { purpose: 'tp2_prospect_search'; input: ProspectSearchPayload }
    ),
  ): Promise<
    | {
        ok: true;
        answerText: string;
        citations: { url: string; title: string; citedText: string }[];
        results: { url: string; title: string; pageAge: string | null }[];
        usage: Usage;
        aiCallId: string;
      }
    | { ok: false; error: AiErrorClass; aiCallId: string | null }
  >;
}

/** Einzige Lesestelle des KI-Ausschlusses je Person (E-a Nr. 5). */
export interface AiExclusionQuery {
  excludedAmong(tx: DbTx | null, ctx: DomainContext, personIds: PersonId[]): Promise<Set<PersonId>>;
}
